// GroupInvitePage — /invite/g/:token public landing.
//
// Mirror of InvitePage but for the Bazooka GROUP link variant. The link
// is shared in a private chat room (Discord, WhatsApp, Telegram, etc.)
// and serves N people on a first-come-first-served basis, up to the
// admin-set `max_claims` cap.
//
// Differences from 1:1 InvitePage:
//   • No pre-personalised first-name greeting — we don't know who
//     clicked. The hero adapts to the chat-room title instead.
//   • Surfaces real-time seat count + platform so the recipient knows
//     this is a chat-room-wide drop, not a personal invite.
//   • Asks for city (required) so the auto-seed city commissions can
//     fire on registration.
//   • On submit, posts to /auth/register with `group_invite_token`
//     instead of `invite_token`.
//
// Same per-claim benefits as the 1:1 path: 90-day Producer comp +
// 1,600 cr. Backend enforces seat cap atomically and idempotent per
// user_id.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PartyPopper, Sparkles, ArrowRight, AlertTriangle, Globe, Users } from "lucide-react";
import api from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";

const GroupInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [homeCity, setHomeCity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/bazooka/group-invite/${token}`)
      .then((r) => {
        if (!alive) return;
        setInvite(r.data || {});
      })
      .catch((err) => {
        if (!alive) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "This group invite isn't valid.";
        setLoadError({ status: status || 0, detail });
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!username.trim() || !email.trim() || !password) {
      toast.error("Please fill in every field.");
      return;
    }
    if (!homeCity.trim()) {
      toast.error("Your home city is required so we can drop 3 starter commissions in your area.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/register", {
        username: username.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        password,
        group_invite_token: token,
        home_city: homeCity.trim(),
      });
      const data = r.data || {};
      try {
        if (data.access_token) {
          localStorage.setItem("globaleyes_token", data.access_token);
        }
      } catch { /* private-mode storage block */ }
      const granted = data.chairman_invite_redemption?.total_credits_granted;
      toast.success(
        granted
          ? `Welcome! +${granted} cr landed and you're a comped Producer for 90 days.`
          : "Welcome! Your account is ready.",
        { duration: 6000 },
      );
      window.location.replace("/dashboard?welcome=chairman_group");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Signup failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // 410-style states: revoked / expired / exhausted — collapse to a
  // friendly "sorry, this one's done" panel that points the visitor at
  // normal signup so they don't bounce.
  const status = invite?.status;
  if (loadError || (status && status !== "active")) {
    const detail = loadError?.detail || (
      status === "exhausted" ? "All the seats from this chat-room invite have been claimed."
      : status === "revoked" ? "This group invite was revoked by the admin."
      : status === "expired" ? "This group invite has expired."
      : "This group invite isn't valid."
    );
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <Card className="bg-gray-900 border-amber-500/40 max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2">Group invite unavailable</h1>
            <p className="text-gray-400 text-sm mb-4">{detail}</p>
            <Button onClick={() => navigate("/")} className="bg-emerald-600 hover:bg-emerald-500" data-testid="group-invite-home-btn">
              <Globe className="h-4 w-4 mr-1.5" /> Sign up at GlobalEyes.TV
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const seatsLeft = invite.seats_left ?? 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-950/50 via-gray-950 to-emerald-950/50 px-4 py-8 flex items-start sm:items-center justify-center">
      <div className="max-w-2xl w-full">
        <Card data-testid="group-invite-card"
              className="bg-gray-950/70 backdrop-blur border-amber-500/40 shadow-2xl">
          <CardContent className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <Users className="h-10 w-10 text-amber-300 mx-auto mb-2" />
              <p className="text-amber-300 text-xs uppercase tracking-widest font-semibold mb-1">
                {invite.platform} · Group Invite
              </p>
              <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight">
                You're invited from
                <span className="text-amber-300"> {invite.chat_room_title}</span>
              </h1>
              <p className="text-emerald-300 text-sm mt-2" data-testid="group-invite-seats">
                <strong className="tabular-nums">{seatsLeft}</strong> of {invite.max_claims} seats still open — first come, first claim.
              </p>
            </div>

            <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 sm:p-5 mb-6">
              <p className="text-gray-200 text-sm sm:text-base">Each claim grants:</p>
              <ul className="text-gray-200 text-sm sm:text-base space-y-1.5 mt-2">
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span><strong>Producer subscription — 90 days comped.</strong></span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span><strong>1,600 credits</strong> (1,000 signing bonus + 600 quarterly Producer allocation).</span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span>3 starter video commissions auto-dropped in your area — earn up to 200 cr each.</span>
                </li>
              </ul>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="group-invite-form">
              <div>
                <label className="text-gray-300 text-xs block mb-1">Pick a public username</label>
                <Input
                  value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="yourhandle"
                  data-testid="group-invite-username"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="text-gray-300 text-xs block mb-1">Email</label>
                <Input type="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="group-invite-email"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="text-gray-300 text-xs block mb-1">Choose a strong password</label>
                <Input type="password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  data-testid="group-invite-password"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="text-gray-300 text-xs block mb-1">Your home city (required)</label>
                <Input
                  value={homeCity} onChange={(e) => setHomeCity(e.target.value)}
                  placeholder="e.g. Brooklyn, NY"
                  data-testid="group-invite-home-city"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="address-level2"
                />
                <p className="text-gray-500 text-[11px] mt-1">
                  We use this to drop 3 starter video commissions right around you.
                </p>
              </div>
              <Button type="submit" disabled={submitting}
                      data-testid="group-invite-submit"
                      className="w-full bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-400 hover:to-emerald-400 text-gray-900 font-bold h-12 text-base">
                {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <PartyPopper className="h-5 w-5 mr-2" />}
                Claim my seat
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <p className="text-gray-500 text-[11px] text-center">
                Group invite from <span className="text-gray-300">{invite.platform}</span> · expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "soon"}.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GroupInvitePage;
